// Loading / failed / loaded, in one place.
//
// Every screen in this app is a read, and every read has the same non-happy
// outcomes. Writing them per panel is how one of them ends up rendering
// nothing at all.

import type { ReactNode } from "react";
import { ProblemMessage, Spinner } from "./Feedback.js";

/** Structural subset of `UseQueryResult` — so tests can pass a plain object. */
export interface QueryLike<T> {
  data: T | undefined;
  error: unknown;
  isPending: boolean;
  refetch?: () => unknown;
}

export interface QueryBoundaryProps<T> {
  query: QueryLike<T>;
  /** Completes "Could not …" and "Loading …". Lowercase, e.g. "the deals list". */
  label: string;
  children: (data: T) => ReactNode;
}

export function QueryBoundary<T>({ query, label, children }: QueryBoundaryProps<T>): ReactNode {
  if (query.error !== null && query.error !== undefined) {
    return (
      <ProblemMessage
        error={query.error}
        action={`load ${label}`}
        {...(query.refetch === undefined ? {} : { onRetry: () => void query.refetch?.() })}
      />
    );
  }

  if (query.isPending || query.data === undefined) {
    return <Spinner label={`Loading ${label}…`} />;
  }

  return <>{children(query.data)}</>;
}
